import { createSlice, createAction } from '@reduxjs/toolkit';
import {
  put, select, takeLatest, call,
} from 'redux-saga/effects';
import { wsClient } from 'util/appUtils';
import { buildPreviewUrl } from 'sagas/utils/previewUrlResolver';
import { getLocalizedMessage } from 'util/useLocalizedNotifications';
import { enqueueWarningSnackbar } from 'actions/Notifications';
import {
  changeSearchOnboardingSettings,
  changeRecsOnboardingSettings,
  saveRecsSettings,
  saveSearchSettings,
  updateSearchVisible,
  updateRecsVisible,
  updatePreview,
} from '../actions';

const initialState = {
  isFetched: false,
  isPreviewLoading: false,
  search: {
    completed: true,
  },
  recs: {
    industries: [],
    fields: [],
    audienceSize: null,
    completed: true,
    wizardCompleted: true,
  },
};

const sliceName = 'onboarding';

export const onboardingSearchPreview = createAction(`${sliceName}/onboardingSearchPreview`);
export const onboardingSearchCustomize = createAction(`${sliceName}/onboardingSearchCustomize`);
export const onboardingSearchActivate = createAction(`${sliceName}/onboardingSearchActivate`);
export const onboardingRecsWizardComplete = createAction(`${sliceName}/onboardingRecsWizardComplete`);
export const onboardingRecsPreview = createAction(`${sliceName}/onboardingRecsPreview`);
export const onboardingRecsCustomize = createAction(`${sliceName}/onboardingRecsCustomize`);
export const onboardingRecsActivate = createAction(`${sliceName}/onboardingRecsActivate`);

export const wizardSearchComplete = createAction(`${sliceName}/wizardSearchComplete`);
export const wizardChangeSearchOnboarding = createAction(`${sliceName}/wizardChangeSearchOnboarding`);

const orderSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    setPreviewLoading(state, { payload }) {
      return {
        ...state,
        isPreviewLoading: payload,
      };
    },
    onboardingRecsChange(state, { payload }) {
      return {
        ...state,
        recs: {
          ...state.recs,
          ...payload,
        },

      };
    },
    onboardingSearchChange(state, { payload }) {
      return {
        ...state,
        search: {
          ...state.search,
          ...payload,
        },
      };
    },
    onboardingChange(state, { payload }) {
      return {
        isFetched: true,
        recs: {
          ...state.recs,
          ...payload.recs,
        },
        search: {
          ...state.search,
          ...payload.search,
        },
      };
    },
  },
});

export const { actions, reducer } = orderSlice;
export const {
  setPreviewLoading,
  onboardingChange,
  onboardingRecsChange,
  onboardingSearchChange,
} = actions;
export default reducer;

/**
 * Opens preview URL in a new tab with popup blocker bypass
 * @param {Window} previewWindow - Pre-opened window reference
 * @param {string} url - URL to navigate to
 */
function openPreviewWindow(previewWindow, url) {
  if (previewWindow && !previewWindow.closed) {
    previewWindow.location.href = url;
    previewWindow.focus();
  }
}

/**
 * Closes preview window if open
 * @param {Window} previewWindow - Window reference to close
 */
function closePreviewWindow(previewWindow) {
  if (previewWindow && !previewWindow.closed) {
    previewWindow.close();
  }
}

function* searchPreview({ payload: previewWindow }) {
  try {
    yield put(setPreviewLoading(true));

    const { shopAddress, isCompactMode } = yield select((state) => ({
      shopAddress: state.settings.shopAddress,
      isCompactMode: state.app.isCompactMode,
    }));

    const previewUrl = yield call(buildPreviewUrl, shopAddress, isCompactMode);
    if (!previewUrl) {
      const message = getLocalizedMessage('onboarding.search.cards.step1.noShopAddress');
      yield put(enqueueWarningSnackbar(message, 8000));
      closePreviewWindow(previewWindow);
      return;
    }
    openPreviewWindow(previewWindow, previewUrl);

    const changes = { step1: true };
    yield put(onboardingSearchChange(changes));
    yield put(changeSearchOnboardingSettings(changes));
    yield put(updateSearchVisible(false));
  } catch (err) {
    console.error('searchPreview failed:', err);
    closePreviewWindow(previewWindow);
  } finally {
    yield put(setPreviewLoading(false));
  }
}

function* searchCustomize() {
  try {
    const changes = { step2: true };
    yield put(onboardingSearchChange(changes));
    yield put(changeSearchOnboardingSettings(changes));
  } catch (err) {
    console.log('Failed in onboardingCustomize', err);
  }
}

function* searchActivate() {
  try {
    const { shopId } = yield select((state) => state.settings);
    const changes = { step3: true, completed: true };
    yield put(onboardingSearchChange(changes));
    yield put(changeSearchOnboardingSettings(changes));

    yield put(updateSearchVisible(true));
    yield put(updatePreview(false));
    yield put(saveSearchSettings(shopId));
  } catch (err) {
    console.log('Failed in onboardingActivate', err);
  }
}

function* wizardSearchCompleteHandler() {
  try {
    const changes = { wizardCompleted: true };
    yield put(onboardingSearchChange(changes));
    yield put(changeSearchOnboardingSettings(changes));
  } catch (err) {
    console.log('Failed in wizardSearchComplete', err);
  }
}

function* wizardChangeSearchOnboardingHandler({ payload }) {
  try {
    yield put(onboardingSearchChange(payload));
    yield put(changeSearchOnboardingSettings(payload));
    const { token } = yield select((state) => state.settings);
    const update = {
      search: {
        onboarding: payload,
      },
    };
    const response = yield wsClient.updateShopSettings(token, update);
  } catch (err) {
    console.log('Failed in wizardChangeSearchOnboarding', err);
  }
}

function* recsWizardComplete() {
  try {
    const changes = { wizardCompleted: true };
    yield put(onboardingRecsChange(changes));

    const { shopId } = yield select((state) => state.settings);
    const payload = yield select((state) => state.onboarding.recs);
    yield put(changeRecsOnboardingSettings(payload));
    yield put(saveRecsSettings(shopId));
  } catch (err) {
    console.log('Failed in recsWizardComplete', err);
  }
}

function* recsPreview({ payload: previewWindow }) {
  try {
    yield put(setPreviewLoading(true));

    const { shopAddress, isCompactMode } = yield select((state) => ({
      shopAddress: state.settings.shopAddress,
      isCompactMode: state.app.isCompactMode,
    }));

    const previewUrl = yield call(buildPreviewUrl, shopAddress, isCompactMode);
    if (!previewUrl) {
      const message = getLocalizedMessage('onboarding.search.cards.step1.noShopAddress');
      yield put(enqueueWarningSnackbar(message, 8000));
      closePreviewWindow(previewWindow);
      return;
    }
    openPreviewWindow(previewWindow, previewUrl);

    const changes = { step1: true };
    yield put(onboardingRecsChange(changes));
    yield put(changeRecsOnboardingSettings(changes));
    yield put(updateSearchVisible(false));
  } catch (err) {
    console.error('recsPreview failed:', err);
    closePreviewWindow(previewWindow);
  } finally {
    yield put(setPreviewLoading(false));
  }
}

function* recsCustomize() {
  try {
    const changes = { step2: true };
    yield put(onboardingRecsChange(changes));
    yield put(changeRecsOnboardingSettings(changes));
  } catch (err) {
    console.log('Failed in onboardingCustomize', err);
  }
}

function* recsActivate() {
  try {
    const { shopId } = yield select((state) => state.settings);
    const changes = { step3: true, completed: true };
    yield put(onboardingRecsChange(changes));
    yield put(changeRecsOnboardingSettings(changes));

    yield put(updateRecsVisible(true));
    yield put(updatePreview(false));
    yield put(saveRecsSettings(shopId));
  } catch (err) {
    console.log('Failed in onboardingActivate', err);
  }
}

export function* sagas() {
  yield takeLatest(onboardingSearchPreview().type, searchPreview);
  yield takeLatest(onboardingSearchCustomize().type, searchCustomize);
  yield takeLatest(onboardingSearchActivate().type, searchActivate);
  yield takeLatest(onboardingRecsWizardComplete().type, recsWizardComplete);
  yield takeLatest(onboardingRecsPreview().type, recsPreview);
  yield takeLatest(onboardingRecsCustomize().type, recsCustomize);
  yield takeLatest(onboardingRecsActivate().type, recsActivate);
  yield takeLatest(wizardSearchComplete().type, wizardSearchCompleteHandler);
  yield takeLatest(wizardChangeSearchOnboarding().type, wizardChangeSearchOnboardingHandler);
}
